/**
 * Field-overlap warning before filing a competing change — #37/old-#347
 * (2026-06-11, Pedro's decision: warn, don't block).
 *
 * Multiple pending CRs per entity are a legitimate state; the hazard is
 * filing a change to a FIELD that already has a pending change without
 * knowing — the later approval silently overwrites the earlier one for
 * that field. This helper detects the overlap so the page can ask the
 * maker to confirm. Nothing is ever forbidden.
 *
 * Usage (inside a page's update flow, before mutate):
 *
 *   const overlap = pendingFieldOverlap(updatePayload, data.pending_change_requests);
 *   if (overlap && !(await confirm(overlapConfirmProps(overlap, labelize)))) return;
 */
import type { PendingChangeRequestRef } from '../api/types';

export interface PendingOverlap {
  /** Field names present both in the new payload and in ≥1 pending CR. */
  fields: string[];
  /** The pending CRs that touch those fields (for filer/date wording). */
  crs: PendingChangeRequestRef[];
}

export function pendingFieldOverlap(
  payload: Record<string, unknown>,
  crs: PendingChangeRequestRef[] | undefined | null,
): PendingOverlap | null {
  if (!crs || crs.length === 0) return null;
  const newKeys = new Set(Object.keys(payload));
  const fields = new Set<string>();
  const hits: PendingChangeRequestRef[] = [];
  for (const cr of crs) {
    const overlapping = (cr.changed_fields ?? []).filter((f) => newKeys.has(f));
    if (overlapping.length > 0) {
      overlapping.forEach((f) => fields.add(f));
      hits.push(cr);
    }
  }
  if (hits.length === 0) return null;
  return { fields: [...fields].sort(), crs: hits };
}

/** Build the confirm-modal props with friendly field labels (§3j). */
export function overlapConfirmProps(
  overlap: PendingOverlap,
  labelize: (field: string) => string,
): {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
} {
  const fieldList = overlap.fields.map(labelize).join(', ');
  const filers = overlap.crs.map((cr) => {
    const who = cr.requested_by_name ?? 'another user';
    let when: string;
    try {
      when = new Date(cr.requested_at).toLocaleString();
    } catch {
      when = String(cr.requested_at);
    }
    return `${who} (${when})`;
  });
  const filerList = [...new Set(filers)].join('; ');
  return {
    title: 'A pending change already touches these field(s)',
    message:
      `${fieldList} — already in a pending change filed by ${filerList}. `
      + 'If you file yours too, Compliance will see both, and the later '
      + 'approval overwrites the earlier one for those field(s). File anyway?',
    confirmLabel: 'File anyway',
    cancelLabel: 'Go back',
  };
}
